<template>
  <div>
    <!-- calendar navigation -->
    <capacity-calendar-header
      :current-month="getCurrentMonth"
      @prev="prev()"
      @next="next()"
      @goToToday="goToToday"
    />
    <!-- calendar -->
    <v-row class="no-gutters mt-3">
      <v-col class="col-12">
        <v-calendar
          ref="calendar"
          v-model="start"
          type="month"
          :weekdays="[1, 2, 3, 4, 5, 6, 0]"
          :locale="$store.getters.getLanguageLocale().slice(0, 2)"
          color="primary"
        >
          <!-- day slot -->
          <template #day-label="item">
            <v-row class="no-gutters">
              <!-- day number -->
              <v-col
                class="col-4 offset-4 text-center font-weight-thin"
                :class="{ 'grey--text': isPastDay(item.date) }"
              >
                <v-btn
                  v-if="item.date === moment().format('YYYY-MM-DD')"
                  icon
                  outlined
                  color="primary"
                >
                  {{ item.day }}
                </v-btn>
                <span v-else>
                  {{ item.day }}
                </span>
              </v-col>

              <!-- plus button -->
              <v-col v-if="!hidePlusButton" class="col-4 text-right pt-0 pr-6">
                <v-btn
                  icon
                  x-small
                  class="white--text primary"
                  @click="$emit('click:day', item)"
                >
                  <span class="fal fa-plus" />
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <!-- day body -->
          <template #day="{ date }">
            <!-- sekeleton loader -->
            <v-row v-if="!productCapacitiesReady" class="no-gutters">
              <v-col class="col-12">
                <v-skeleton-loader type="list-item" />
              </v-col>
            </v-row>
            <v-row
              v-else-if="
                calendarEntriesInstance.getCalendarEntryByDate(date).length <=
                calendarEntriesThreshold
              "
              class="no-gutters"
              :style="'min-height:' + maxCalendarEntries * 35 + 'px;'"
              @click="
                (nativeEvent) => {
                  expandCapacities(nativeEvent, date)
                }
              "
            >
              <v-col
                v-for="(
                  calendarEntry, calendarEntryIndex
                ) in calendarEntriesInstance.getCalendarEntryByDate(date)"
                :key="calendarEntryIndex"
                class="col-12"
              >
                <capacity-progress-bar
                  :product="calendarEntry.product"
                  :capacity="calendarEntry.capacity"
                  :event="calendarEntry.event"
                  :startDate="calendarEntry.date"
                  :endDate="calendarEntry.date"
                  :custom-progress-description="
                    customDescription(calendarEntry.product)
                  "
                  class="clickable"
                />
              </v-col>
            </v-row>

            <!-- show capacities in a separate v-menu -->
            <v-row
              v-else
              class="clickable no-gutters"
              :style="'min-height:' + maxCalendarEntries * 35 + 'px;'"
              @click="
                (nativeEvent) => {
                  expandCapacities(nativeEvent, date)
                }
              "
            >
              <!-- iterate the first three capacities -->
              <v-col
                v-for="(
                  firstSelectedCalendarEntry, firstSelectedCalendarEntryIndex
                ) in calendarEntriesInstance.getCalendarEntryByDate(
                  date,
                  calendarEntriesThreshold - 1
                )"
                :key="firstSelectedCalendarEntryIndex"
                class="col-12 pt-0 pb-1 text-center"
              >
                <capacity-progress-bar
                  :product="firstSelectedCalendarEntry.product"
                  :capacity="firstSelectedCalendarEntry.capacity"
                  :event="firstSelectedCalendarEntry.event"
                  :startDate="firstSelectedCalendarEntry.date"
                  :endDate="firstSelectedCalendarEntry.date"
                  :custom-progress-description="
                    customDescription(firstSelectedCalendarEntry.product)
                  "
                  :disabled="isPastDay(date)"
                />
              </v-col>

              <!-- show more -->
              <v-col
                v-if="isDateAvailable(date)"
                class="col-12 py-1 text-center"
                :class="{ 'grey--text': isPastDay(date) }"
              >
                <span class="fal fa-chevron-down mr-1" />
                {{ $t('capacityCalendar.showAll') }}
              </v-col>
            </v-row>
          </template>
        </v-calendar>
        <!-- expand all capacities -->
        <v-menu
          v-model="selectedOpen"
          :close-on-content-click="false"
          :activator="selectedElement"
          offset-y
          top
          :nudge-left="menuNudgeLeft"
        >
          <capacity-override
            id="capacityOverrideId"
            ref="capacityOverride"
            :selected-date="selectedDate"
            :calendar-entries="calendarEntriesInstance"
            :products="products"
            :disabled="isPastDay(selectedDate)"
            :hide-sibling-product="hideSiblingProduct"
            @capacityOverridden="capacityOverriden()"
            @loadAdditionalProduct="
              (id) => {
                this.$emit('loadAdditionalProduct', id)
                repositioningMenu(true)
              }
            "
            @removeAdditionalProduct="
              (id) => {
                this.$emit('removeAdditionalProduct', id)
                repositioningMenu(false)
              }
            "
            @showOverrideForm="repositioningMenu"
          />
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment'
import CapacityProgressBar from '../products/CapacityProgressBar'
import _ from 'lodash'
import CapacityCalendarHeader from './CapacityCalendarHeader'
import CapacityOverride from './CapacityOverride'
import { CapacityCalendarEntries } from '../../classes-internal/capacities/CapacityCalendarEntries'

export default {
  name: 'CapacityCalendar',
  components: { CapacityOverride, CapacityCalendarHeader, CapacityProgressBar },
  props: {
    // array with product instances
    products: {
      type: Array,
      required: false,
      default: () => {
        return []
      },
    },

    // do not display the button left side hand of the day number
    hidePlusButton: {
      type: Boolean,
      required: false,
      default: false,
    },

    hideDescription: {
      type: Boolean,
      required: false,
      default: false,
    },
    disallowDateChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    // hide the button to load sibling products
    // passing this prop to CapacityOverride.vue
    hideSiblingProduct: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      // calendar
      moment: moment,
      start: moment().format('YYYY-MM-DD'),
      // threshold from which the day's calendar body is switched to "load more.."
      calendarEntriesThreshold: 3,
      // capacity override
      selectedElement: null,
      selectedOpen: false,
      selectedDate: null,

      // Type: Event.js
      eventInstanceToEdit: null,
      calenderMoveIndex: 0,
      calendarEntriesInstance: new CapacityCalendarEntries(),
      // re-positioning menu
      menuNudgeLeft: 0,
      blockCapacitiesReady: false,
    }
  },

  computed: {
    productCapacitiesReady() {
      if (this.blockCapacitiesReady) return false
      for (let i = 0; i < this.products.length; i++) {
        let product = this.products[i]
        if (!product.getProductCapacity()) return false
      }
      return true
    },
    calendarEntriesArray() {
      this.calendarEntriesInstance.init(this.products)
      return this.calendarEntriesInstance.getCalendarEntries()
    },

    getCurrentMonth() {
      return moment(this.start)
    },

    // what is the maximal entries on one day => used to calculate the first days height on the calendar
    // which defines the other day's heights
    maxCalendarEntries() {
      let groupedEntries = _.groupBy(
        this.calendarEntriesArray,
        (entry) => entry.date
      )
      let maxEntries = 0
      for (let groupEntry in groupedEntries) {
        if (groupedEntries[groupEntry].length > maxEntries)
          maxEntries = groupedEntries[groupEntry].length
      }
      return maxEntries > this.calendarEntriesThreshold
        ? this.calendarEntriesThreshold
        : maxEntries
    },
    todayMs() {
      return new Date().setHours(0, 0, 0, 0)
    },
  },

  watch: {
    start() {
      this.dateChanged()
    },
    // on overridden window closes
    selectedOpen() {
      if (!this.selectedOpen) {
        this.$refs.capacityOverride.reset()
      }
    },
  },

  mounted() {
    EventBus.$on('Product:CapacityFetched', () => {
      this.blockCapacitiesReady = false
    })
  },

  beforeDestroy() {
    EventBus.$off('Product:CapacityFetched')
  },

  methods: {
    /**
     *
     * @param dateToCheck: YYYY-MM-DD
     */
    isPastDay(dateToCheck) {
      return new Date(dateToCheck).getTime() < this.todayMs
    },
    isDateAvailable(date) {
      date = moment(date).format('YYYY-MM-DD')
      for (let i = 0; i < this.products.length; i++) {
        if (
          this.products[i]
            .getAvailableDates('dateString')
            .find((validityDate) => validityDate === date)
        )
          return true
      }
      return false
    },
    capacityOverriden() {
      this.blockCapacitiesReady = true
      this.$emit('capacityOverridden')
      // close modal
      this.selectedOpen = false
    },
    // user clicked on the day body if more than n capacities
    expandCapacities(nativeEvent, selectedDate) {
      // pattern by vuetify.com
      const open = () => {
        this.selectedElement = nativeEvent.target
        setTimeout(() => {
          this.selectedOpen = true
          this.$nextTick(() => {
            this.selectedDate = selectedDate
          })
        }, 10)
      }
      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 10)
      } else {
        open()
      }
      nativeEvent.stopPropagation()
    },

    repositioningMenu(fadeIn) {
      if (fadeIn) {
        let element = document.getElementById('capacityOverrideId')
        let elementRight = element.getBoundingClientRect().right
        let windowWidth = window.outerWidth

        // check right side
        let margin = windowWidth - elementRight
        if (margin < 340) {
          this.menuNudgeLeft = 480 - margin
        }
      } else {
        this.menuNudgeLeft = 0
      }
    },

    dateChanged() {
      this.blockCapacitiesReady = true
      this.$emit('date-changed', this.determineFromToDates())
    },

    // helper method of dateChanged()
    determineFromToDates() {
      let dates = {
        from: moment(this.start),
        to: moment(this.start),
      }
      dates.from = dates.from.date(1)
      dates.to = dates.to.endOf('month').hour(23).minute(59)
      return dates
    },
    goToToday() {
      if (this.calenderMoveIndex > 0) {
        this.$refs.calendar.move(-this.calenderMoveIndex)
      } else {
        this.$refs.calendar.move(
          // yes on purpose ;)
          -this.calenderMoveIndex
        )
      }
      this.calenderMoveIndex = 0
    },
    prev() {
      this.$refs.calendar.prev()
      --this.calenderMoveIndex
    },
    next() {
      this.$refs.calendar.next()
      ++this.calenderMoveIndex
    },
    // show product name, if more than 2 products are shown in the calendar
    customDescription(product) {
      if (this.products.length > 1 && product) {
        return product.getTitle()
      }
      return null
    },
  },
}
</script>
<style lang="scss">
.v-calendar-weekly__day-label {
  height: 3rem !important;
}
</style>
